import { useEffect, useState } from "react";
import { JoinForm } from "@/components/JoinForm";
import { useNavigate, Link, useParams } from "react-router-dom";
import { LegalFooter } from "@/components/LegalFooter";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { HeroMockup } from "@/components/HeroMockup";

function AgencyPanel({ e }) {
  return (
    <div className="relative hidden lg:flex flex-col justify-between p-12 xl:p-14 overflow-hidden" style={{ backgroundColor: e.dark_color }} data-testid="entry-panel">
      <div className="absolute -right-28 -top-28 w-[420px] h-[420px] rounded-full opacity-[0.14]" style={{ backgroundColor: e.accent_color || e.color }} aria-hidden="true" />
      <div className="relative">{e.logo_url
        ? <img src={`${process.env.REACT_APP_BACKEND_URL}${e.logo_url}`} alt={e.name} className="h-16 max-w-[240px] object-contain bg-white rounded-xl p-2" data-testid="entry-logo" />
        : <span className="text-white font-extrabold text-2xl">{e.name}</span>}</div>
      <h1 className="relative text-white font-extrabold tracking-tight text-4xl xl:text-5xl leading-[1.08] max-w-lg" data-testid="entry-name">{e.name}</h1>
      <p className="relative text-xs text-white/50">{e.white_label ? "" : "powered by ImoDesk"}</p>
    </div>
  );
}

export default function Login() {
  const { slug } = useParams();
  const [entry, setEntry] = useState(null);
  useEffect(() => {
    if (!slug) { return; }
    import("@/lib/api").then(({ default: api }) => api.get(`/public/agency-entry/${slug}`).then(({ data }) => setEntry(data)).catch(() => setEntry(null)));
  }, [slug]);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [le, setLe] = useState("");
  const [lp, setLp] = useState("");

  const [forgot, setForgot] = useState(false);
  const [join, setJoin] = useState(false);
  const [fe, setFe] = useState("");
  const [sent, setSent] = useState(false);

  const doForgot = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { default: api } = await import("@/lib/api");
      await api.post("/auth/forgot-password", { email: fe });
      setSent(true);
    } catch (e) { setErr(apiErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await login(le, lp); navigate("/inicio"); }
    catch (e) { setErr(apiErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const field = "mt-1.5 h-11 focus-visible:ring-[#1FBF8F]";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white font-body" data-testid={entry ? "login-agency" : "login-default"}>
      {entry ? <AgencyPanel e={entry} /> : (
      <div className="relative hidden lg:flex flex-col justify-between p-12 xl:p-14 overflow-hidden" style={{ backgroundColor: "#13224A" }}>
        <div className="absolute -right-28 -top-28 w-[420px] h-[420px] rounded-full opacity-[0.12]" style={{ backgroundColor: "#1FBF8F" }} aria-hidden="true" />
        <Link to="/" className="relative"><Logo dark size={34} /></Link>
        <div className="relative text-white">
          <h1 className="font-extrabold tracking-tight text-4xl xl:text-5xl leading-[1.08] max-w-lg" data-testid="home-title">
            Da angariação à escritura, <span style={{ color: "#1FBF8F" }}>sem nada esquecido.</span>
          </h1>
          <p className="mt-4 text-white/75 text-lg max-w-md" data-testid="home-subtitle">
            Uma plataforma para gerir todo o processo imobiliário num único lugar.
          </p>
          <div className="mt-10 max-w-[540px]"><HeroMockup /></div>
        </div>
        <p className="relative text-xs text-white/50">A ferramenta organiza e lembra; não dá aconselhamento jurídico.</p>
      </div>)}

      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          {entry ? (
            <div className="lg:hidden mb-8">{entry.logo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${entry.logo_url}`} alt={entry.name} className="h-10 max-w-[200px] object-contain" data-testid="entry-logo-mobile" /> : <span className="font-extrabold text-xl" style={{ color: entry.dark_color }}>{entry.name}</span>}
              {!entry.white_label && <div className="text-[11px] text-slate-400 mt-1">powered by ImoDesk</div>}</div>
          ) : <Link to="/" className="lg:hidden inline-flex mb-8"><Logo size={34} /></Link>}

          {join ? (
            <>
              <JoinForm />
              <button type="button" onClick={() => setJoin(false)} className="mt-4 text-sm text-[#13224A]/80 hover:text-[#1FBF8F] underline underline-offset-4 transition-colors" data-testid="join-back">
                Já tenho conta · Entrar
              </button>
            </>
          ) : (
          <Tabs defaultValue="entrar">

            {err && (
              <div className="mb-4 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2" data-testid="auth-error">
                {err}
              </div>
            )}

            <TabsContent value="entrar">
              {!forgot ? (
                <>
                  <h2 className="font-extrabold tracking-tight text-2xl text-[#13224A] mb-1">Bem-vindo de volta</h2>
                  <p className="text-sm text-slate-500 mb-6">Entre com o seu email e palavra-passe.</p>
                  <form onSubmit={doLogin} className="space-y-4">
                    <div>
                      <Label>Email</Label>
                      <Input type="email" value={le} onChange={(e) => setLe(e.target.value)} required className={field} data-testid="login-email" placeholder="o-seu@email.pt" />
                    </div>
                    <div>
                      <Label>Palavra-passe</Label>
                      <Input type="password" value={lp} onChange={(e) => setLp(e.target.value)} required className={field} data-testid="login-password" placeholder="••••••••" />
                    </div>
                    <button type="submit" disabled={loading} data-testid="login-submit" style={entry ? { backgroundColor: entry.color } : undefined}
                      className="w-full bg-[#13224A] text-white py-3 rounded-full font-semibold hover:bg-[#0D1836] transition-colors disabled:opacity-60">
                      {loading ? "A entrar…" : "Entrar"}
                    </button>
                  </form>
                  <button type="button" onClick={() => { setForgot(true); setErr(""); setSent(false); }}
                    data-testid="forgot-link"
                    className="mt-4 text-sm text-[#13224A]/80 hover:text-[#1FBF8F] underline underline-offset-4 transition-colors">
                    Esqueci-me da palavra-passe
                  </button>
                </>
              ) : (
                <>
                  <h2 className="font-extrabold tracking-tight text-2xl text-[#13224A] mb-1">Repor palavra-passe</h2>
                  <p className="text-sm text-slate-500 mb-6">Enviamos-lhe um link por email para definir uma nova.</p>
                  {sent ? (
                    <div className="text-sm bg-green-50 text-green-800 border border-green-200 rounded-lg px-4 py-3" data-testid="forgot-sent">
                      Se existir uma conta com esse email, enviámos um link de reposição. Verifique a sua caixa de entrada.
                    </div>
                  ) : (
                    <form onSubmit={doForgot} className="space-y-4">
                      <div>
                        <Label>Email</Label>
                        <Input type="email" value={fe} onChange={(e) => setFe(e.target.value)} required className={field} data-testid="forgot-email" placeholder="o-seu@email.pt" />
                      </div>
                      <button type="submit" disabled={loading} data-testid="forgot-submit"
                        className="w-full bg-[#13224A] text-white py-3 rounded-full font-semibold hover:bg-[#0D1836] transition-colors disabled:opacity-60">
                        {loading ? "A enviar…" : "Enviar link de reposição"}
                      </button>
                    </form>
                  )}
                  <button type="button" onClick={() => { setForgot(false); setErr(""); }}
                    className="mt-4 text-sm text-[#13224A]/80 hover:text-[#1FBF8F] underline underline-offset-4 transition-colors">
                    Voltar a entrar
                  </button>
                </>
              )}
            </TabsContent>
          </Tabs>
          )}
          <p className="mt-8 text-sm text-slate-600 text-center" data-testid="login-no-access">
            {!join && <>A sua agência já usa o ImoDesk? <button type="button" onClick={() => { setJoin(true); setErr(""); }} className="font-semibold text-[#13224A] underline underline-offset-4 hover:text-[#1FBF8F]" data-testid="join-open">Registar-me</button><br /></>}
            Ainda não tem acesso? <Link to="/#demonstracao" className="font-semibold text-[#13224A] underline underline-offset-4 hover:text-[#1FBF8F]" data-testid="login-demo-link">Peça uma demonstração.</Link>
          </p>
          <LegalFooter className="mt-6 text-center [&>nav]:justify-center" />
        </div>
      </div>
    </div>
  );
}
